import { graphHooks } from '@tunasong/graph-lib/react'
import type { Calendar, ManyOfManyPoll, Persisted } from '@tunasong/schemas'
import { Button, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import { getWinnerIndices } from './poll-outcome.js'
import { AddToCalendarDialog } from './add-to-calendar-dialog.js'
import { formatAddedToCalendarLabel, formatAddToCalendarTrigger } from './build-calendar-events.js'
import styles from './add-to-calendar-action.module.css'

export interface AddToCalendarActionProps {
  poll: Persisted<ManyOfManyPoll>
}

/**
 * The one action a closed poll carries, and the only one it may carry twice by accident.
 *
 * Nothing links a `calendarevent` back to the poll that produced it, so a second confirm
 * duplicates every rehearsal in the band's calendar with no error and no visible difference.
 * The handoff therefore records where it wrote — see `pollSchema.calendarId` — and this
 * component reports that instead of offering to do it again.
 *
 * The local copy exists because the Finalize layout holds the poll in component state, so the
 * persisted field will not come back through the prop until the page is next mounted; without
 * it the button would remain on screen after a successful write. It is set only after the
 * dialog reports a completed write, never on the click.
 */
export const AddToCalendarAction: FC<AddToCalendarActionProps> = props => {
  const { poll } = props

  const [showDialog, setShowDialog] = useState(false)
  const [addedTo, setAddedTo] = useState<string | undefined>(undefined)

  const updateEntity = graphHooks.useEntityUpdate<ManyOfManyPoll>({ debounceDelay: 0 })

  const calendarId = poll.calendarId ?? addedTo
  const { entity: calendar } = graphHooks.useEntity<Calendar>(calendarId)

  const handleAdded = (target: string) => {
    setAddedTo(target)
    updateEntity(poll.id, { calendarId: target })
  }

  if (calendarId) {
    return (
      <Typography variant="body2" className={styles.added}>
        {formatAddedToCalendarLabel(calendar)}
      </Typography>
    )
  }

  return (
    <>
      {/*
        Inline width, and never stretched. On the Finalize layout the action sits in a stretching
        flex column, which made it span the full content width and outrank the winning date — the
        action becoming the loudest thing on a page whose job is to state an outcome. Owned here
        rather than at either host, so both surfaces get the same button.
      */}
      <span className={styles.trigger}>
        <Button variant="outlined" color="secondary" onClick={() => setShowDialog(true)}>
          {formatAddToCalendarTrigger(getWinnerIndices(poll).length)}
        </Button>
      </span>
      <AddToCalendarDialog poll={poll} open={showDialog} onClose={() => setShowDialog(false)} onAdded={handleAdded} />
    </>
  )
}

export default AddToCalendarAction
